import { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'jobsurf_email_verify_dismissed_until';

/**
 * Slim soft-gate reminder banner. Shows on internal pages for users with
 * email_verified === false. User can resend the verification email or
 * dismiss for 24h. Does NOT block any product functionality.
 */
export default function EmailVerificationBanner() {
  const { user, getAuthHeaders } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return until > Date.now();
    } catch { return false; }
  });
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified !== false || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { headers: getAuthHeaders() });
      toast.success('Verification email sent. Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send. Please try again in a minute.');
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-200"
      data-testid="email-verification-banner"
    >
      <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
        <Mail className="w-4 h-4 text-amber-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-amber-100">Please verify your email</p>
        <p className="text-xs text-amber-200/80 truncate">We sent a verification link to <strong>{user.email}</strong>. You can keep using JobSurf in the meantime.</p>
      </div>
      <button
        onClick={handleResend}
        disabled={sending}
        className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-black bg-amber-300 hover:bg-amber-200 rounded-full px-3 py-1.5 transition-colors disabled:opacity-60"
        data-testid="resend-verification-btn"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Resend email
      </button>
      <button
        onClick={handleResend}
        disabled={sending}
        className="sm:hidden inline-flex items-center justify-center text-xs font-bold text-black bg-amber-300 rounded-full px-3 py-1.5 disabled:opacity-60"
        aria-label="Resend verification email"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Resend'}
      </button>
      <button
        onClick={handleDismiss}
        className="text-amber-300/60 hover:text-amber-100 p-1 shrink-0"
        aria-label="Dismiss for 24 hours"
        data-testid="dismiss-verification-banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
